<template>
  <div class="signup-component">
    <!-- STEP 1: Email and Password -->
    <form v-if="step === 1" @submit.prevent.stop="sendOtpFlow">
      <div class="form-group mb-3">
        <label class="form-label text-muted small fw-bold text-uppercase">Email Address</label>
        <input
          type="email"
          class="form-control tc-signup-input"
          v-model="attributes.email"
          placeholder="name@company.com"
          :disabled="loading"
          required
        />
      </div>

      <div class="form-group mb-3">
        <label class="form-label text-muted small fw-bold text-uppercase">Password</label>
        <input
          type="password"
          class="form-control tc-signup-input"
          v-model="attributes.password"
          placeholder="Create a strong password"
          :disabled="loading"
          @input="checkPassword"
          required
        />
        <p v-if="showPasswordRequirements" class="text-muted small mt-2">
          Password must be at least 6 characters long and include an uppercase letter, a number, and a special character (!@#$%^&*).
        </p>
      </div>

      <div class="form-group mb-4">
        <label class="form-label text-muted small fw-bold text-uppercase">Confirm Password</label>
        <input
          type="password"
          class="form-control tc-signup-input"
          v-model="attributes.confirmPassword"
          placeholder="Confirm your password"
          :disabled="loading"
          required
        />
      </div>

      <button type="submit" class="btn tc-signup-btn-primary-gradient w-100 rounded-pill py-2 fw-bold" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        Send Verification Code <i class="fa fa-arrow-right ms-2" v-if="!loading"></i>
      </button>
    </form>

    <!-- STEP 2: OTP Verification -->
    <form v-if="step === 2" @submit.prevent.stop="verifyOtpFlow">
      <div class="alert alert-info border-0 tc-signup-bg-primary-subtle tc-signup-text-primary mb-4 rounded-3 text-center">
        <i class="fa fa-envelope-open-text mb-2" style="font-size: 1.5rem;"></i>
        <p class="mb-0 small fw-medium">We've sent a 6-digit verification code to<br/><strong>{{ attributes.email }}</strong></p>
      </div>

      <div class="form-group mb-4">
        <label class="form-label text-muted small fw-bold text-uppercase text-center w-100">Verification Code</label>
        <input
          type="text"
          class="form-control tc-signup-input text-center fs-4 fw-bold tc-signup-letter-spacing-2"
          v-model="attributes.otp"
          placeholder="------"
          maxlength="6"
          :disabled="loading"
          required
        />
      </div>

      <button type="submit" class="btn tc-signup-btn-primary-gradient w-100 rounded-pill py-2 fw-bold" :disabled="loading || attributes.otp.length !== 6">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        Verify & Sign Up <i class="fa fa-check ms-2" v-if="!loading"></i>
      </button>
      
      <div class="text-center mt-3">
        <button type="button" class="btn btn-link text-muted small text-decoration-none" @click="step = 1" :disabled="loading">
          <i class="fa fa-arrow-left me-1"></i> Use a different email
        </button>
      </div>
    </form>

    <div class="mt-2 text-center pt-4 border-top mt-4">
      <p class="text-muted mb-0">
        Already have an account?
        <button @click="toggleForm" class="btn btn-link tc-signup-text-primary fw-bold p-0 ms-1 text-decoration-none">Login</button>
      </p>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue';

export default {
  data() {
    return {
      step: 1,
      loading: false,
      attributes: {
        email: '',
        password: '',
        confirmPassword: '',
        otp: ''
      },
      showPasswordRequirements: true,
    };
  },
  setup() {
    const { loginJwt } = inject('user');
    return { loginJwt };
  },
  methods: {
    validatePassword(password) {
      const passwordRegex = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{6,}$/;
      return passwordRegex.test(password);
    },

    checkPassword() {
      this.showPasswordRequirements = !this.validatePassword(this.attributes.password);
    },

    async sendOtpFlow() {
      if (this.attributes.password !== this.attributes.confirmPassword) {
        this.$bvToast.toast('Passwords do not match!', { title: 'Error', variant: 'danger', solid: true });
        return;
      }

      if (!this.validatePassword(this.attributes.password)) {
        this.$bvToast.toast('Password does not meet requirements.', { title: 'Security', variant: 'warning', solid: true });
        return;
      }

      this.loading = true;
      try {
        await this.$xhr.post('/api/auth/send-otp', {
          email: this.attributes.email
        });
        this.step = 2;
        this.$bvToast.toast(`OTP sent to ${this.attributes.email}`, { title: 'Success', variant: 'success', solid: true });
      } catch (error) {
        const msg = error.response?.data?.error?.message || error.response?.data?.message || 'Failed to send verification code';
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },

    async verifyOtpFlow() {
      this.loading = true;
      try {
        const response = await this.$xhr.post('/api/auth/register-with-otp', {
          email: this.attributes.email,
          password: this.attributes.password,
          otp: this.attributes.otp
        });

        localStorage.setItem('authToken', response.data.jwt);
        localStorage.setItem('userProfile', JSON.stringify(response.data.user));

        this.$bvToast.toast(`Account created successfully!`, {
          title: 'Welcome',
          variant: 'success',
          solid: true,
        });

        this.$router.push({ name: 'home' });
      } catch (error) {
        const msg = error.response?.data?.error?.message || error.response?.data?.message || 'Invalid verification code';
        this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },

    toggleForm() {
      this.$router.push({ name: 'login' });
    },
  },
};
</script>

<style scoped>
.tc-signup-input {
  background-color: #F8F9FC;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
  padding: 0.8rem 1.2rem;
  font-size: 1rem;
  color: #11142D;
  transition: all 0.2s;
}
.tc-signup-input:focus {
  border-color: #6C5DD3;
  box-shadow: 0 0 0 0.25rem rgba(108, 93, 211, 0.15);
  background-color: #ffffff;
}

.tc-signup-btn-primary-gradient {
  background: #6C5DD3;
  border: none;
  color: white;
  transition: transform 0.2s, box-shadow 0.2s;
}
.tc-signup-btn-primary-gradient:hover:not(:disabled) {
  transform: translateY(-2px);
  background: #5b4eb8;
  box-shadow: 0 6px 20px rgba(108, 93, 211, 0.3);
  color: white;
}

.tc-signup-text-primary {
  color: #6C5DD3 !important;
}

.tc-signup-bg-primary-subtle {
  background-color: #F0EDFF !important;
}

.tc-signup-letter-spacing-2 {
  letter-spacing: 0.5rem;
}
</style>